"use client"

import { useEffect, useState, ChangeEvent } from "react"
import type { FC } from "react"
import {useMediaQuery, useTheme} from "@mui/material"
import SearchResults from "./SearchResults"
import SearchResultsMobile from "../SearchResultsMobile/SearchResultsMobile"
import { getStagesByName } from "../../Services/api"
import "./SearchResults.css"
import "../SearchResultsMobile/SearchResultsMobile.css"

interface SearchResultItem {
  project_id: number
  project_name: string
  stage_number?: number
  stage_name?: string
  section_number?: number
  section_name?: string
  subsection_number?: number
  subsection_name?: string
  status: number
}

interface SearchResultsContainerProps {
  searchTerm: string
  results: SearchResultItem[]
  isLoading?: boolean
  isOpen: boolean
  onClose: () => void
}

const SearchResultsContainer: FC<SearchResultsContainerProps> = ({
  searchTerm,
  results,
  isLoading = false,
  isOpen,
  onClose
}) => {
  const [searchInput, setSearchInput] = useState<string>(searchTerm)
  const [localResults, setLocalResults] = useState<SearchResultItem[]>(results)
  const [loadingLocal, setLoadingLocal] = useState<boolean>(false)

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setSearchInput(searchTerm)
    setLocalResults(results)
  }, [searchTerm, results])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value)
  }

  const handleSearchSubmit = async () => {
    const term = searchInput.trim()
    if (term.length < 1) return

    setLoadingLocal(true)
    try {
      const data = await getStagesByName(term)
      setLocalResults(data)
    } catch {
      setLocalResults([])
    } finally {
      setLoadingLocal(false)
    }
  }

  if (!isOpen) return null

  if (loadingLocal || isLoading) {
    return (
      <div className="popup-overlay">
        <div className="search-results-container">
          <div className="search-results-header">
            <h3>טוען נתונים...</h3>
          </div>
          <div className="no-results">אנא המתן</div>
        </div>
      </div>
    )
  }

  return isMobile ? (
    <SearchResultsMobile
      results={localResults}
      searchTerm={searchInput}
      onClose={onClose}
      searchValue={searchInput}
      onSearchChange={handleSearchChange}
      onSearchSubmit={handleSearchSubmit}
    />
  ) : (
    <SearchResults
      results={localResults}
      searchTerm={searchInput}
      onClose={onClose}
    />
  )
}

export default SearchResultsContainer
